import pluralize from 'pluralize';

import { type Clips } from '@air/api';

import { type WorkspaceAgentAssetPreviewCardMeta } from '~/components/v2/components/workspace/workspace-agent-panel/utils/getWorkspaceAgentAssetMetaLabel';
import { type WorkspaceAgentPillClip } from '~/components/v2/components/workspace/workspace-agent-panel/utils/toWorkspaceAgentPillClipsById';
import { joinWorkspaceAgentLocationCrumbs } from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentLocationLabels';
import { type ClipGetOptionsWithBoards } from '~/swr-hooks/useGetClipWithNoWorkspaceId';

/**
 * The card's own board contract: the three fields the location line draws, narrowed from the board
 * rows the per-open `Clips.getWithNoWorkspaceId` returns.
 */
export type WorkspaceAgentAssetPreviewCardBoard = Pick<
  Awaited<ReturnType<typeof Clips.getWithNoWorkspaceId<ClipGetOptionsWithBoards>>>['boards'][number],
  'id' | 'library' | 'title'
>;

/** Everything `WorkspaceAgentAssetPreviewCard` draws for a resolved asset; the card owns the handlers. */
export type WorkspaceAgentAssetPreviewCardContent = WorkspaceAgentAssetPreviewCardMeta & {
  commentCount: number | null;
  locationLabel: string | null;
  thumbnailUrl: string | null;
  title: string;
  versionCount: number | null;
};

/**
 * Names a board only when the asset is on exactly one; on several it counts them instead, and on none it
 * says nothing. The rows come back ordered by last edit, so naming the first of several would be arbitrary
 * — a card that read `Marketing / Hero shots` would be telling the truth about one of the boards the asset
 * happens to sit in, and a different one tomorrow.
 */
const getAssetLocationLabel = (boards: WorkspaceAgentAssetPreviewCardBoard[] | null) => {
  if (!boards?.length) {
    return null;
  }

  if (boards.length > 1) {
    return `In ${pluralize('board', boards.length, true)}`;
  }

  const [board] = boards;

  return joinWorkspaceAgentLocationCrumbs([board.library?.title, board.title]);
};

export type WorkspaceAgentAssetPreviewCardContentParams = {
  /** Fetched per open rather than read off `clip`: the batched clip row carries no boards. `null` until it lands. */
  boards: WorkspaceAgentAssetPreviewCardBoard[] | null;
  clip: WorkspaceAgentPillClip;
  /** Fetched per open rather than read off `clip`: the batched clip row never carries it. */
  commentCount: number | null;
};

export const toWorkspaceAgentAssetPreviewCardContent = ({
  boards,
  clip,
  commentCount,
}: WorkspaceAgentAssetPreviewCardContentParams): WorkspaceAgentAssetPreviewCardContent => ({
  commentCount,
  duration: clip.duration,
  ext: clip.ext,
  height: clip.height,
  locationLabel: getAssetLocationLabel(boards),
  size: clip.size,
  thumbnailUrl: clip.thumbnailUrl,
  title: clip.name,
  type: clip.type,
  versionCount: clip.versionCount,
  width: clip.width,
});
